import type { WizardIndex } from './types';

/**
 * The step forward from `from`.
 *
 * `children` take precedence: the first child is the default forward move, and `next` is the forward
 * move for a step with no children to descend into — the shared-tail case the edge exists for. The
 * reverse precedence shipped first and was wrong twice over: it contradicted the documented rule on
 * `children`, and it made a step's entire subtree unreachable except by naming a child explicitly,
 * which is a structural dead zone nothing would report.
 *
 * `requested` picks a branch by key and must be a child of `from` (or its `next` edge) — an unknown
 * key throws rather than silently going somewhere else, because a typo'd branch key would otherwise
 * read as "the default child" and be nearly invisible in review.
 */
export function nextKey<K extends string>(index: WizardIndex<K>, from: K, requested?: K): K | null {
  const step = index.byKey.get(from);
  if (step === undefined) return null;

  const children = step.children ?? [];
  if (requested !== undefined) {
    const named = children.find((c) => c.key === requested);
    if (named !== undefined) return named.key;
    if (step.next === requested) return requested;
    throw new Error(`Wizard step "${requested}" is not reachable from "${from}"`);
  }

  return children[0]?.key ?? step.next ?? null;
}

/**
 * The step Back returns to: the structural parent, or — for a step reached across a `next` edge — the
 * step that named it. Null at a root with no inbound edge.
 *
 * Without the reverse edge, Back from a shared tail went to the tail's structural parent (or nowhere
 * at all), silently discarding the branch the user actually came from. `indexTree` guarantees a step
 * has at most one of the two, so there is nothing to choose between here.
 */
export function prevKey<K extends string>(index: WizardIndex<K>, from: K): K | null {
  return index.parentOf.get(from) ?? index.nextFrom.get(from) ?? null;
}

/**
 * Where cancelling lands: the `cancelTo` of the nearest sub-flow root on the way back from `from`.
 *
 * Walks the same chain as {@link prevKey}, reverse `next` edges included, so crossing an edge out of
 * a sub-flow keeps the sub-flow's cancel target instead of dropping it — the alternative left a step
 * showing a Back button with no cancel semantics behind it. `indexTree` proves that chain acyclic, so
 * this terminates without a visited set.
 *
 * Null means "not inside a sub-flow", and the caller should close the wizard instead — which is the
 * only behaviour rudder-webapp's store has.
 */
export function exitKey<K extends string>(index: WizardIndex<K>, from: K): K | null {
  let cursor: K | null = from;
  while (cursor !== null) {
    const step = index.byKey.get(cursor);
    if (step?.cancelTo !== undefined) return step.cancelTo;
    cursor = prevKey(index, cursor);
  }
  return null;
}
